import { $generateHtmlFromNodes, $generateNodesFromDOM } from "@lexical/html";
import { $isTableNode } from "@lexical/table";
import { FormControlProps, OutlinedInput, OutlinedInputProps, useForkRef } from "@mui/material";
import { $createParagraphNode, $getRoot, $isDecoratorNode, $isElementNode, LexicalEditor } from "lexical";
import { map } from "ramda";
import { isNotNil } from "ramda-adjunct";
import React, { forwardRef, type ReactNode, useEffect, useMemo, useRef } from "react";
import { FormControlWrapper, InputWrapperProps } from "../FormControlWrapper/FormControlWrapper";
import { MinimalTextEditor } from "./variants/MinimalTextEditor";
import { SimpleTextEditor } from "./variants/SimpleTextEditor";
import { PlainTextEditor } from "@sinch/components/form/TextEditor/variants/PlainTextEditor";

export interface TextEditorProps<TVariant extends "simple" | "minimal" | "plain" = "simple">
  extends Omit<OutlinedInputProps, "slotProps" | "slots" | "onChange">,
    InputWrapperProps {
  FormControlProps?: FormControlProps;
  onChange?: (value: string) => void;
  value?: string;
  maxLength?: number;
  /**
   * variant of editor, differ in list of utilities and features (bold, italic, lists, etc..)
   */
  variant?: TVariant;
  /**
   * Position of toolbar
   */
  toolbarPosition?: "top" | "bottom";
  /**
   * Add placeholders option to Simple variant
   */
  placeholders?: TVariant extends "simple" | "plain" ? { label: ReactNode; value: string; size?: number }[] : never;
  editorRef?: React.Ref<LexicalEditor | null>;
  secondaryToolbar?: ReactNode;
  /**
   * Adds an "insert image" toolbar button to the Simple variant. Picked images
   * are embedded inline as base64 data URLs. Only meaningful for the "simple" variant.
   */
  enableImageInsert?: boolean;
  /**
   * Indicates whether the text content should be plain text.
   * When set to true, the content will not include any formatting, styling, or markup.
   * Used in scenarios where raw text data is required or expected.
   * If undefined or false, the content may include formatting or HTML markup.
   */
  plainText?: boolean;
}

/**
 * Rich text editor based on Lexical.
 */
export const TextEditor = <TVariant extends "simple" | "minimal" | "plain" = "simple">({
  label,
  FormHelperTextProps: formHelperTextProps,
  FormControlProps: formControlProps,
  helperText,
  errorMessage,
  error,
  required,
  infoTooltip,
  onChange,
  value,
  slotProps,
  slots,
  maxLength,
  variant,
  toolbarPosition = "top",
  placeholders,
  editorRef,
  secondaryToolbar,
  enableImageInsert,
  plainText,
  ...props
}: TextEditorProps<TVariant>) => {
  const ref = useRef<LexicalEditor>(null);
  const setRef = useForkRef(ref, editorRef);
  const stringValue = useRef("");

  useEffect(() => {
    const editor = ref.current;
    if (editor) {
      editor.update(
        () => {
          if (value && value !== stringValue.current) {
            // If value is plain text (no HTML tags), wrap in <p> and convert \n to <br/>
            const isHtml = /<[a-z][\s\S]*>/i.test(value);
            const htmlInput = isHtml ? value : `<p>${value.replace(/\n/g, "<br/>")}</p>`;
            // In the browser you can use the native DOMParser API to parse the HTML string.
            const parser = new DOMParser();
            const dom = parser.parseFromString(htmlInput, "text/html");

            // Once you have the DOM instance it's easy to generate LexicalNodes.
            const nodes = $generateNodesFromDOM(editor, dom);

            const root = $getRoot();
            root.clear();

            const rootChildren = map((node) => {
              if ($isElementNode(node) || $isDecoratorNode(node)) {
                return node;
              } else {
                const p = $createParagraphNode();
                p.append(node);
                return p;
              }
            }, nodes);

            // Append nodes directly to root without focusing
            root.append(...rootChildren);
          }
        },
        { discrete: true }
      );

      const removeUpdateListener = editor.registerUpdateListener(({ editorState, dirtyElements }) => {
        if (isNotNil(dirtyElements) && dirtyElements.size > 0) {
          editorState.read(() => {
            if (plainText) {
              const textValue = $getRoot().getTextContent();
              stringValue.current = textValue;
              onChange?.(textValue);
            } else {
              const htmlValue = $generateHtmlFromNodes(editor, null);
              const parser = new DOMParser();
              const element = parser.parseFromString(htmlValue, "text/html");
              stringValue.current = htmlValue;
              // Treat the editor as empty only when there is neither text nor embedded
              // media. Images carry no text content, so an image-only body must not collapse to "".
              const isEmpty = element.body.textContent?.trim() === "" && element.body.querySelector("img") === null;
              const result = isEmpty ? "" : htmlValue;
              onChange?.(result);
            }
          });
        }
      });

      return () => removeUpdateListener();
    }
  }, [
    value,
    onChange,
    plainText,
    variant /* keep variant here, for some reason it broke when changing variant on fly */,
  ]);

  // This effect is used to add a paragraph node at the end of the editor if there is only one child and it's a table.
  // It's fix problem with table node not being deleteable when copied from external editor as Excel or Calc
  useEffect(() => {
    const editor = ref.current;
    if (editor) {
      editor.registerUpdateListener(({ editorState }) => {
        editorState.read(() => {
          const root = $getRoot();
          const children = root.getChildren();

          // Check if there is only one child and it's a table
          if (children.length === 1 && $isTableNode(children[0])) {
            editor.update(() => {
              // Add a paragraph node at the end
              root.append($createParagraphNode());
            });
          }
        });
      });
    }
  }, []);

  // noinspection LocalVariableNamingConventionJS
  const InputSlot = useMemo(() => {
    if (variant === "plain") {
      // oxlint-disable-next-line @typescript-eslint/no-unused-vars
      const memoizedEditor = forwardRef((inputProps, _) => (
        <PlainTextEditor {...inputProps} innerRef={setRef} toolbarPosition={toolbarPosition} />
      ));
      memoizedEditor.displayName = "MinimalTextEditor";
      return memoizedEditor;
    }
    if (variant === "minimal") {
      // oxlint-disable-next-line @typescript-eslint/no-unused-vars
      const memoizedEditor = forwardRef((inputProps, _) => (
        <MinimalTextEditor {...inputProps} innerRef={setRef} toolbarPosition={toolbarPosition} />
      ));
      memoizedEditor.displayName = "MinimalTextEditor";
      return memoizedEditor;
    }
    // oxlint-disable-next-line @typescript-eslint/no-unused-vars
    const memoizedEditor = forwardRef((inputProps, _) => (
      <SimpleTextEditor {...inputProps} innerRef={setRef} toolbarPosition={toolbarPosition} />
    ));
    memoizedEditor.displayName = "SimpleTextEditor";
    return memoizedEditor;
  }, [variant, setRef, toolbarPosition]);

  return (
    <FormControlWrapper
      error={error}
      errorMessage={errorMessage}
      FormHelperTextProps={formHelperTextProps}
      helperText={helperText}
      infoTooltip={infoTooltip}
      label={label}
      required={required}
      slotProps={slotProps}
      slots={slots}
      {...formControlProps}
    >
      <OutlinedInput
        {...props}
        fullWidth
        slotProps={{
          input: {
            maxLength,
            secondaryToolbar,
            placeholders,
            enableImageInsert,
          } as NonNullable<OutlinedInputProps["slotProps"]>["input"],
        }}
        slots={{
          input: InputSlot,
        }}
      />
    </FormControlWrapper>
  );
};

declare module "@mui/material/InputBase" {
  interface InputBaseComponentsPropsOverrides {
    secondaryToolbar?: ReactNode;
    enableImageInsert?: boolean;
  }
}
